import { useMemo } from "react";
import {
  format,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  getDay,
} from "date-fns";
import { cn } from "@/lib/utils";
import type { ZoomLevel } from "@/hooks/useTankPlanningState";

interface TimelineHeaderProps {
  startDate: Date;
  endDate: Date;
  zoomLevel: ZoomLevel;
  dayWidth: number;
  vesselLabelWidth: number;
}

export function TimelineHeader({
  startDate,
  endDate,
  zoomLevel,
  dayWidth,
  vesselLabelWidth,
}: TimelineHeaderProps) {
  // Generate day columns
  const days = useMemo(() => {
    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [startDate, endDate]);

  // Generate month markers for week/month view
  const months = useMemo(() => {
    return eachMonthOfInterval({ start: startDate, end: endDate });
  }, [startDate, endDate]);

  // Generate week markers for week view
  const weeks = useMemo(() => {
    return eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 1 });
  }, [startDate, endDate]);

  // Calculate total width
  const totalWidth = days.length * dayWidth;

  return (
    <div className="bg-card">
      {/* Month row - label locks to left edge of visible timeline area */}
      <div
        className="flex h-6 border-b bg-muted/50"
        style={{ width: totalWidth }}
      >
        {months.map((month, idx) => {
          const monthDays = days.filter((d) => isSameMonth(d, month));
          const width = monthDays.length * dayWidth;
          // Pixel offset of this month's cell from the start of the timeline area
          const monthOffset = days.findIndex(d => isSameMonth(d, month)) * dayWidth;

          return (
            <div
              key={month.toISOString()}
              className="border-r h-6 shrink-0 relative overflow-hidden"
              style={{ width }}
            >
              <div
                className="absolute inset-y-0 flex items-center text-xs font-medium text-muted-foreground whitespace-nowrap pl-1 pointer-events-none"
                style={{ left: `max(0px, calc(var(--scroll-left, 0px) - ${monthOffset}px))` }}
              >
                {format(month, "MMMM yyyy")}
              </div>
            </div>
          );
        })}
      </div>

      {/* Day/Week row */}
      {zoomLevel === 'day' && (
        <div className="flex h-10" style={{ width: totalWidth }}>
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className={cn(
                "flex flex-col items-center justify-center border-r",
                isToday(day) && "bg-primary/15",
                getDay(day) === 0 || getDay(day) === 6
                  ? "bg-muted/60"
                  : ""
              )}
              style={{ width: dayWidth }}
            >
              <span className="text-[10px] text-muted-foreground">
                {format(day, "EEE")}
              </span>
              <span
                className={cn(
                  "text-xs font-medium",
                  isToday(day) && "text-primary font-bold"
                )}
              >
                {format(day, "d")}
              </span>
            </div>
          ))}
        </div>
      )}

      {zoomLevel === 'week' && (
        <div className="flex h-8" style={{ width: totalWidth }}>
          {days.map((day) => {
            const isMonday = getDay(day) === 1;
            const isSunday = getDay(day) === 0;
            const isSaturday = getDay(day) === 6;

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "flex flex-col items-center justify-center border-r text-[10px]",
                  isToday(day) && "bg-primary/15",
                  isSaturday && "border-r-2 border-r-muted-foreground/30",
                  (isSaturday || isSunday) && "bg-muted/60"
                )}
                style={{ width: dayWidth }}
              >
                <span className={cn(
                  "text-muted-foreground",
                  isMonday && "font-medium",
                  isToday(day) && "text-primary font-bold"
                )}>
                  {format(day, "d")}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {zoomLevel === 'month' && (
        <div className="flex h-6" style={{ width: totalWidth }}>
          {days.map((day, idx) => {
            const isFirstOfMonth = day.getDate() === 1;
            const isMonday = getDay(day) === 1;
            const isSunday = getDay(day) === 0;

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "flex items-center justify-center text-[8px]",
                  isToday(day) && "bg-primary/10",
                  isFirstOfMonth && "border-l-2 border-l-muted-foreground/50",
                  isSunday && "border-r border-r-muted-foreground/20",
                  getDay(day) === 0 || getDay(day) === 6
                    ? "bg-muted/50"
                    : ""
                )}
                style={{ width: dayWidth }}
              >
                {isMonday && idx % 2 === 0 && (
                  <span className="text-muted-foreground">
                    {format(day, "d")}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
